"use client"

import { useMemo, type ReactNode } from "react"
import { useLocale, useMessages, useTranslations } from "next-intl"
import { useTranslatedErrorMessage } from "@/hooks/use-translated-error-message"
import { getUiComponentTranslators } from "@/lib/i18n/ui-component-translations"
import {mergeCatalogs} from "@/lib/i18n/merge-catalogs"
import englishUi from "@/translations/en/ui-components.json"
import { UiControlLabelsContext, UI_CONTROL_KEYS, type UiControlLabels } from "./ui-control-labels-context"

const identityError = (message: string) => message

/** Main-app error summaries share the same context as generic controls. */
export function MainUiControlLabelsProvider({ children }: { children: ReactNode }) {
  const { message: error, field: fieldError } = useTranslatedErrorMessage()
  return <UiControlLabelsProvider error={error} fieldError={fieldError}>{children}</UiControlLabelsProvider>
}

/** Standalone controls need only their own messages; English defaults work outside the app. */
export function UiControlLabelsProvider({ children, error = identityError, fieldError = identityError }: {
  children: ReactNode
  error?: (message: string) => string
  fieldError?: (message: string) => string
}) {
  const t = useTranslations("UiControls")
  const locale = useLocale()
  const messages = useMessages()
  const value = useMemo<UiControlLabels>(() => ({
    // Object.fromEntries loses the keys of this complete, fixed registry.
    labels: Object.fromEntries(UI_CONTROL_KEYS.map(key => [key, t(key)])) as UiControlLabels["labels"],
    locale, error, fieldError,
    translations: getUiComponentTranslators(mergeCatalogs(englishUi, {UiComponents: messages.UiComponents}), locale),
    range: (from, to, total) => t("showingRange", { from, to, total }),
  }), [error, fieldError, locale, messages, t])
  return <UiControlLabelsContext.Provider value={value}>{children}</UiControlLabelsContext.Provider>
}
